import React, { useState, useEffect } from 'react';
import { Form2Data } from '../types';
import { STANDORTE } from '../constants';
import SearchableSelect from '../../components/SearchableSelect';

interface Step1Props {
    onSubmit: (data: Partial<Form2Data>) => void;
    initialData: Partial<Form2Data>;
    isSubmitting: boolean;
    submissionError: string | null;
}

const Step1Participation: React.FC<Step1Props> = ({ onSubmit, initialData, isSubmitting, submissionError }) => {
    const [formData, setFormData] = useState<Partial<Form2Data>>(initialData);
    const [errors, setErrors] = useState<Partial<Record<keyof Form2Data, string>>>({});
    const [isValidating, setIsValidating] = useState(false);
    const [standorteList, setStandorteList] = useState<string[]>(() => {
        if (initialData.standort && !STANDORTE.includes(initialData.standort)) {
            return [...STANDORTE, initialData.standort].sort((a, b) => a.localeCompare(b, 'de', {sensitivity: 'base'}));
        }
        return STANDORTE;
    });

    useEffect(() => {
        setFormData(initialData);
    }, [initialData]);

    const checkEmailExists = async (email: string): Promise<string | null> => {
        if (!email) {
            return 'Bitte geben Sie eine E-Mail-Adresse an.';
        }
        if (!/\S+@\S+\.\S+/.test(email)) {
            return 'Bitte geben Sie eine gültige E-Mail-Adresse an.';
        }
        try {
            const res = await fetch('/api/forms/registration/check-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });

            if (!res.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await res.json();
            if (data.exists) {
                return 'Diese E-Mail-Adresse ist bereits registriert.';
            }
        } catch (error) {
            console.error('Email validation error:', error);
            return 'Fehler bei der E-Mail-Validierung.';
        }
        return null;
    };

    const validate = async (): Promise<boolean> => {
        const newErrors: Partial<Record<keyof Form2Data, string>> = {};
        
        if (!formData.teilnahme) {
            newErrors.teilnahme = 'Bitte wählen Sie Ihre Teilnahme aus.';
        }
        
        if (formData.teilnahme === 'ja') {
            if (!formData.anrede) newErrors.anrede = 'Bitte wählen Sie eine Anrede aus.';
            if (!formData.vorname) newErrors.vorname = 'Bitte geben Sie einen Vornamen an.';
            if (!formData.nachname) newErrors.nachname = 'Bitte geben Sie einen Nachnamen an.';

            const emailError = await checkEmailExists(formData.email || '');
            if (emailError) {
                newErrors.email = emailError;
            }

            if (!formData.standort) newErrors.standort = 'Bitte wählen Sie einen Standort aus.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        setFormData(prev => ({ ...prev, [name]: value }));

        if (errors[name as keyof Form2Data]) {
            const newErrors = { ...errors };
            delete newErrors[name as keyof Form2Data];
            setErrors(newErrors);
        }
    };

    const handleEmailBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
        const email = e.target.value;
        const emailError = await checkEmailExists(email);
        setErrors(prev => ({ ...prev, email: emailError || undefined }));
    };

    const handleStandortChange = (value: string) => {
        setFormData(prev => ({ ...prev, standort: value }));
        if (errors.standort) {
            const newErrors = { ...errors };
            delete newErrors.standort;
            setErrors(newErrors);
        }
    };

    const handleSubmit = async () => {
        setIsValidating(true);
        if (await validate()) {
            onSubmit(formData);
        }
        setIsValidating(false);
    };

    const getInputClass = (fieldName: keyof Form2Data) =>
        `mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-magenta focus:border-magenta sm:text-sm text-gray-900 ${errors[fieldName] ? 'border-red-500' : ''}`;

    return (
        <div className="max-w-4xl mx-auto bg-white p-6 md:p-8 rounded-lg shadow-lg">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-custom mb-6 text-center">Teilnahme</h1>
            <form onSubmit={(e) => e.preventDefault()}>
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Werden Sie an der Jahresauftaktveranstaltung der Limbach Gruppe vom 23. bis 24. Februar 2026 teilnehmen?</label>
                        <div className="mt-2 flex gap-4">
                            <label className="inline-flex items-center">
                                <input type="radio" name="teilnahme" value="ja" onChange={handleChange} checked={formData.teilnahme === 'ja'} className="form-radio" />
                                <span className="ml-2">Ja</span>
                            </label>
                            <label className="inline-flex items-center">
                                <input type="radio" name="teilnahme" value="nein" onChange={handleChange} checked={formData.teilnahme === 'nein'} className="form-radio" />
                                <span className="ml-2">Nein</span>
                            </label>
                        </div>
                        {errors.teilnahme && <p className="mt-1 text-sm text-red-600">{errors.teilnahme}</p>}
                    </div>

                    {formData.teilnahme === 'ja' && (
                        <>
                            <div>
                                <label htmlFor="anrede" className="block text-sm font-medium text-gray-700">Anrede</label>
                                <select id="anrede" name="anrede" value={formData.anrede || ''} onChange={handleChange} className={getInputClass('anrede')}>
                                    <option value="" disabled>Bitte wählen Sie Ihre Anrede</option>
                                    <option value="frau">Frau</option>
                                    <option value="herr">Herr</option>
                                    <option value="divers">Divers</option>
                                </select>
                                {errors.anrede && <p className="mt-1 text-sm text-red-600">{errors.anrede}</p>}
                            </div>
                            <div>
                                <label htmlFor="titel" className="block text-sm font-medium text-gray-700">Titel (optional)</label>
                                <input type="text" id="titel" name="titel" value={formData.titel || ''} onChange={handleChange} className={getInputClass('titel')} />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="vorname" className="block text-sm font-medium text-gray-700">Vorname</label>
                                    <input type="text" id="vorname" name="vorname" value={formData.vorname || ''} onChange={handleChange} className={getInputClass('vorname')} required />
                                    {errors.vorname && <p className="mt-1 text-sm text-red-600">{errors.vorname}</p>}
                                </div>
                                <div>
                                    <label htmlFor="nachname" className="block text-sm font-medium text-gray-700">Nachname</label>
                                    <input type="text" id="nachname" name="nachname" value={formData.nachname || ''} onChange={handleChange} className={getInputClass('nachname')} required />
                                    {errors.nachname && <p className="mt-1 text-sm text-red-600">{errors.nachname}</p>}
                                </div>
                            </div>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700">E-Mail-Adresse</label>
                                <input type="email" id="email" name="email" value={formData.email || ''} onChange={handleChange} onBlur={handleEmailBlur} className={getInputClass('email')} required />
                                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                            </div>
                            <div>
                                <label htmlFor="standort" className="block text-sm font-medium text-gray-700">Standort</label>
                                <SearchableSelect
                                    id="standort"
                                    name="standort"
                                    options={standorteList}
                                    value={formData.standort || ''}
                                    onChange={handleStandortChange}
                                    className={getInputClass('standort')}
                                    required
                                />
                                {errors.standort && <p className="mt-1 text-sm text-red-600">{errors.standort}</p>}
                            </div>
                            <div>
                                <label htmlFor="telefonnummer" className="block text-sm font-medium text-gray-700">Telefonnummer (beruflich) (optional)</label>
                                <input type="tel" id="telefonnummer" name="telefonnummer" value={formData.telefonnummer || ''} onChange={handleChange} className={getInputClass('telefonnummer')} />
                            </div>
                        </>
                    )}
                </div>
                {submissionError && <p className="mt-4 text-sm text-red-600 text-center">{submissionError}</p>}
                <div className="mt-8 border-t pt-6">
                    <button type="button" onClick={handleSubmit} disabled={isValidating || isSubmitting} className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-magenta hover:bg-berry focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-magenta disabled:opacity-50">
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Wird gesendet...</span>
                            </>
                        ) : isValidating ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Wird geprüft...</span>
                            </>
                        ) : (
                            'Weiter'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Step1Participation;